import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Grid,
  MenuItem,
  Chip,
  Link,
  CircularProgress,
  useTheme,
  FormHelperText,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ApartmentIcon from "@mui/icons-material/Apartment";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { iconForAmenityLabel, orderedSizeUnitEntries } from "./listNewAssetCreationData";
import { useCountriesQuery, useStatesQuery, useCitiesQuery } from "../../../redux/api/authApi";
import { toast } from "../../common/AppToast";
import { filledFieldWrapSx, filledUploadDropzoneSx } from "./listNewAssetFilledSurface";

const TOTAL_STEPS = 8;
const MAX_FILE_MB = 10;

const labelSx = { fontWeight: 600, fontSize: 14, color: "text.primary", mb: 0.75 };

function locationRowId(row) {
  if (!row || typeof row !== "object") return "";
  return row._id || row.id || "";
}

function locationRowName(row) {
  if (!row || typeof row !== "object") return "";
  return row.name || row.label || "";
}

function RequiredLabel({ children }) {
  return (
    <Typography component="span" sx={labelSx}>
      {children}{" "}
      <Box component="span" sx={{ color: "error.main" }}>
        *
      </Box>
    </Typography>
  );
}

export default function ListNewAssetStep2({
  draft,
  creationData,
  onDraftChange,
  nextStep,
  prevStep,
  isEditMode = false,
}) {
  const theme = useTheme();
  const TEAL = theme.palette.brand.cyan;
  const TEAL_HOVER = theme.palette.brand.cyanDark;

  // Cyan-only input theme for the Property Specifications section —
  // overrides MUI's default blue focus/hover colors with the theme's cyan shades.
  const cyanInputSx = {
    "& .MuiOutlinedInput-root": {
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.brand.cyan,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.brand.cyanDark,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.brand.cyanDark,
    },
    "& .MuiSelect-icon": {
      color: theme.palette.brand.cyan,
    },
  };

  // Apply a white fill to the rounded inner input element so it doesn't
  // bleed square white corners over a tinted parent background.
  const whiteInputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiOutlinedInput-input": {
      backgroundColor: theme.palette.background.paper,
      borderRadius: "inherit",
    },
  };

  const assetName = draft.assetName ?? "";
  const description = draft.description ?? "";
  const streetAddress = draft.streetAddress ?? "";
  const addressCountryId = draft.addressCountryId ?? "";
  const addressStateId = draft.addressStateId ?? "";
  const addressCityId = draft.addressCityId ?? "";
  const zipCode = draft.zipCode ?? "";
  const propertyType = draft.propertyType ?? "";
  const sizeValue = draft.sizeValue ?? "";
  const sizeUnit = draft.sizeUnit ?? "sq ft";
  const propertyTypeOptions = creationData?.propertyTypeOptions ?? [];
  const sizeUnitEntries = useMemo(
    () => orderedSizeUnitEntries(creationData?.sizeUnitOptions),
    [creationData?.sizeUnitOptions]
  );
  const featureOptions = creationData?.featuresAndAmenitiesOptions ?? [];

  const selectedAmenities = new Set(draft.amenityIds || []);
  const regaDeedNumber = draft.regaDeedNumber ?? "";
  const regaCrNumber = draft.regaCrNumber ?? "";
  const regaOwnerName = draft.regaOwnerName ?? "";
  const regaCertificateFile = draft.regaCertificateFile ?? null;
  const regaCertInputRef = useRef(null);
  const maxChars = 500;
  const [errors, setErrors] = useState({});

  const { data: countriesRes, isLoading: countriesLoading, isError: countriesError } =
    useCountriesQuery();
  const countries = useMemo(() => {
    const raw = countriesRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [countriesRes]);

  const { data: statesRes, isLoading: statesLoading, isFetching: statesFetching } = useStatesQuery(
    addressCountryId,
    { skip: !addressCountryId }
  );
  const states = useMemo(() => {
    const raw = statesRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [statesRes]);

  const { data: citiesRes, isLoading: citiesLoading, isFetching: citiesFetching } = useCitiesQuery(
    addressStateId,
    { skip: !addressStateId }
  );
  const cities = useMemo(() => {
    const raw = citiesRes?.data;
    return Array.isArray(raw) ? raw : [];
  }, [citiesRes]);

  const selectedCountry = useMemo(
    () => countries.find((c) => locationRowId(c) === addressCountryId),
    [countries, addressCountryId]
  );
  const selectedState = useMemo(
    () => states.find((s) => locationRowId(s) === addressStateId),
    [states, addressStateId]
  );
  const selectedCity = useMemo(
    () => cities.find((c) => locationRowId(c) === addressCityId),
    [cities, addressCityId]
  );

  const toggleAmenity = (label) => {
    const next = new Set(draft.amenityIds || []);
    if (next.has(label)) next.delete(label);
    else next.add(label);
    onDraftChange({ amenityIds: Array.from(next) });
  };

  const isAllowedFile = (file) => {
    const okType =
      file.type === "application/pdf" ||
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/jpg";
    if (okType) return true;
    return /\.(pdf|jpe?g|png)$/i.test(file.name);
  };

  const handleRegaCertificateUpload = (file) => {
    if (!file) return;
    if (!isAllowedFile(file)) {
      toast.error("Please upload a PDF, JPG, or PNG file.");
      return;
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File must be ${MAX_FILE_MB}MB or smaller.`);
      return;
    }
    onDraftChange({ regaCertificateFile: file });
    setErrors((p) => ({ ...p, regaCertificateFile: "" }));
  };

  const handleNext = () => {
    const newErrors = {};
    if (!assetName.trim()) newErrors.assetName = "Asset name is required.";
    if (!description.trim()) newErrors.description = "Description is required.";
    if (!streetAddress.trim()) newErrors.streetAddress = "Street address is required.";
    if (!addressCountryId) newErrors.addressCountryId = "Country is required.";
    if (!addressStateId) newErrors.addressStateId = "State / Region is required.";
    if (!addressCityId) newErrors.addressCityId = "City is required.";
    if (!zipCode.trim()) newErrors.zipCode = "Zip / Postal code is required.";
    if (!propertyType) newErrors.propertyType = "Property type is required.";
    if (!regaDeedNumber.trim()) newErrors.regaDeedNumber = "Deed number is required.";
    if (!regaCrNumber.trim()) newErrors.regaCrNumber = "CR number is required.";
    if (!regaOwnerName.trim()) newErrors.regaOwnerName = "Owner name is required.";
    const hasRegaFile =
      regaCertificateFile ||
      (isEditMode && Boolean(draft._editMeta?.regaCertificateUrl));
    if (!hasRegaFile) newErrors.regaCertificateFile = "Please upload the REGA certificate.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    nextStep();
  };

  const set = (patch) => onDraftChange(patch);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1100, mx: "auto", minHeight: "100vh" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          type="button"
          underline="none"
          onClick={prevStep}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: TEAL,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            border: "none",
            background: "none",
            p: 0,
            "&:hover": { color: TEAL_HOVER },
          }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 16 }} />
          Back
        </Link>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label="Real Estate"
            size="small"
            sx={{
              bgcolor: (theme) => theme.palette.brand.cyanSoft,
              color: (theme) => theme.palette.brand.cyanDark,
              fontWeight: 600,
              fontSize: 12,
              height: 26,
            }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.muted" }}>
            Step 2 of {TOTAL_STEPS}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 999,
              bgcolor: i <= 1 ? TEAL : theme.palette.border.light,
            }}
          />
        ))}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3, md: 4 },
          borderRadius: "14px",
          bgcolor: "background.paper",
          boxShadow: (theme) => theme.palette.shadow.cardStrong,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "text.primary", mb: 0.75 }}
        >
          Asset Details
        </Typography>
        <Typography variant="body2" sx={{ color: "text.muted", mb: 3 }}>
          Provide complete information about your asset
        </Typography>

        {/* RERA banner */}
        {/* <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: (theme) => theme.palette.accent.warning.bg,
            border: (theme) =>
              `1px solid ${theme.palette.accent.warning.border}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.5,
              bgcolor: (theme) => theme.palette.accent.warning.iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: (theme) => theme.palette.accent.warning.icon,
              flexShrink: 0,
            }}
          >
            <ApartmentIcon />
          </Box>
          <Typography
            sx={{
              flex: 1,
              fontSize: 14,
              color: (theme) => theme.palette.accent.warning.text,
              minWidth: 0,
            }}
          >
            Official Property? Fetch details directly from RERA records to auto-fill this form.
          </Typography>
          <Button
            variant="contained"
            size="medium"
            startIcon={<SearchIcon />}
            onClick={handleFetchRera}
            sx={{
              flexShrink: 0,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: (theme) => theme.palette.accent.warning.actionBg,
              boxShadow: "none",
              "&:hover": {
                bgcolor: (theme) => theme.palette.accent.warning.actionBgHover,
                boxShadow: "none",
              },
            }}
          >
            Fetch Details
          </Button>
        </Paper> */}

        {/* Asset name & description */}
        <Stack spacing={2.5} sx={{ mb: 1 }}>
          <Box sx={filledFieldWrapSx(theme, !!assetName.trim())}>
            <RequiredLabel>Asset Name</RequiredLabel>
            <TextField
              variant="standard"
              placeholder="e.g., Dubai Solar Farm Project"
              fullWidth
              value={assetName}
              onChange={(e) => { set({ assetName: e.target.value }); setErrors((p) => ({ ...p, assetName: "" })); }}
              InputProps={{ disableUnderline: true }}
              sx={{
                mt: 1,
                "& .MuiInputBase-input": {
                  px: 2,
                  py: 1.25,
                  fontSize: 14,
                },
              }}
            />
            {errors.assetName && <FormHelperText error sx={{ mt: 0.5 }}>{errors.assetName}</FormHelperText>}
          </Box>
          <Box sx={filledFieldWrapSx(theme, !!description.trim())}>
            <RequiredLabel>Description</RequiredLabel>
            <TextField
              variant="standard"
              fullWidth
              multiline
              minRows={3}
              placeholder="Detailed description of your asset..."
              value={description}
              onChange={(e) => { set({ description: e.target.value.slice(0, maxChars) }); setErrors((p) => ({ ...p, description: "" })); }}
              InputProps={{ disableUnderline: true }}
              sx={{
                mt: 1,
                "& .MuiInputBase-input": {
                  px: 2,
                  py: 1.25,
                  fontSize: 14,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: "text.muted", mt: 0.5, display: "block" }}
            >
              {description.length}/{maxChars} characters
            </Typography>
            {errors.description && <FormHelperText error sx={{ mt: 0.5 }}>{errors.description}</FormHelperText>}
          </Box>
        </Stack>

        {/* Complete Address */}
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            bgcolor: (theme) => theme.palette.brand.cyanSoft,
            border: (theme) => `1px solid ${theme.palette.accent.info.border}`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <ApartmentIcon
              sx={{
                color: (theme) => theme.palette.accent.info.icon,
                fontSize: 22,
              }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: (theme) => theme.palette.accent.info.text,
              }}
            >
              Complete Address
            </Typography>
          </Stack>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12 }}>
              <Box sx={filledFieldWrapSx(theme, !!streetAddress.trim())}>
                <RequiredLabel>Street Address</RequiredLabel>
                <TextField
                  placeholder="e.g., 123 Marina Boulevard"
                  fullWidth
                  value={streetAddress}
                  onChange={(e) => { set({ streetAddress: e.target.value }); setErrors((p) => ({ ...p, streetAddress: "" })); }}
                  sx={{ mt: 1, ...whiteInputSx, ...cyanInputSx }}
                />
                {errors.streetAddress && <FormHelperText error sx={{ mt: 0.5 }}>{errors.streetAddress}</FormHelperText>}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={filledFieldWrapSx(theme, !!addressCountryId)}>
                <RequiredLabel>Country</RequiredLabel>
              <TextField
                select
                fullWidth
                disabled={countriesLoading || countriesError}
                value={addressCountryId}
                onChange={(e) => {
                  const id = e.target.value;
                  const c = countries.find((x) => locationRowId(x) === id);
                  set({
                    addressCountryId: id,
                    country: locationRowName(c),
                    addressStateId: "",
                    state: "",
                    addressCityId: "",
                    city: "",
                  });
                  setErrors((p) => ({ ...p, addressCountryId: "" }));
                }}
                sx={{
                  mt: 1,
                  ...whiteInputSx,
                  ...cyanInputSx,
                }}
                SelectProps={{
                  displayEmpty: true,
                  IconComponent: LocationOnOutlinedIcon,
                  renderValue: (v) => {
                    if (countriesLoading) {
                      return (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CircularProgress size={18} />
                          <Typography sx={{ color: "text.muted" }}>Loading countries…</Typography>
                        </Stack>
                      );
                    }
                    if (!v) {
                      return <Typography sx={{ color: "text.faint" }}>Select country…</Typography>;
                    }
                    return (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {selectedCountry?.flag ? (
                          <Typography component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
                            {selectedCountry.flag}
                          </Typography>
                        ) : null}
                        <Typography component="span">
                          {locationRowName(selectedCountry) || v}
                        </Typography>
                      </Stack>
                    );
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select country…</em>
                </MenuItem>
                {countries.map((c) => {
                  const cid = locationRowId(c);
                  if (!cid) return null;
                  return (
                    <MenuItem key={cid} value={cid}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {c.flag ? (
                          <Typography component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
                            {c.flag}
                          </Typography>
                        ) : null}
                        <span>{locationRowName(c)}</span>
                      </Stack>
                    </MenuItem>
                  );
                })}
              </TextField>
              {countriesError ? (
                <Typography
                  variant="caption"
                  sx={{ color: "error.main", mt: 0.5, display: "block" }}
                >
                  Could not load countries. Refresh and try again.
                </Typography>
              ) : null}
              {errors.addressCountryId && <FormHelperText error sx={{ mt: 0.5 }}>{errors.addressCountryId}</FormHelperText>}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={filledFieldWrapSx(theme, !!addressStateId)}>
                <RequiredLabel>State / Region</RequiredLabel>
              <TextField
                select
                fullWidth
                disabled={!addressCountryId || statesLoading || statesFetching}
                value={addressStateId}
                onChange={(e) => {
                  const id = e.target.value;
                  const s = states.find((x) => locationRowId(x) === id);
                  set({
                    addressStateId: id,
                    state: locationRowName(s),
                    addressCityId: "",
                    city: "",
                  });
                  setErrors((p) => ({ ...p, addressStateId: "" }));
                }}
                sx={{ mt: 1, ...whiteInputSx, ...cyanInputSx }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) => {
                    if (!addressCountryId) {
                      return <Typography sx={{ color: "text.faint" }}>Select a country first</Typography>;
                    }
                    if (statesLoading || statesFetching) {
                      return (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CircularProgress size={18} />
                          <Typography sx={{ color: "text.muted" }}>Loading states…</Typography>
                        </Stack>
                      );
                    }
                    if (!v) {
                      return <Typography sx={{ color: "text.faint" }}>Select state…</Typography>;
                    }
                    return locationRowName(selectedState) || v;
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select state…</em>
                </MenuItem>
                {states.map((s) => {
                  const sid = locationRowId(s);
                  if (!sid) return null;
                  return (
                    <MenuItem key={sid} value={sid}>
                      {locationRowName(s)}
                    </MenuItem>
                  );
                })}
              </TextField>
              {errors.addressStateId && <FormHelperText error sx={{ mt: 0.5 }}>{errors.addressStateId}</FormHelperText>}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={filledFieldWrapSx(theme, !!addressCityId)}>
                <RequiredLabel>City</RequiredLabel>
              <TextField
                select
                fullWidth
                disabled={!addressStateId || citiesLoading || citiesFetching}
                value={addressCityId}
                onChange={(e) => {
                  const id = e.target.value;
                  const cityRow = cities.find((x) => locationRowId(x) === id);
                  set({
                    addressCityId: id,
                    city: locationRowName(cityRow),
                  });
                  setErrors((p) => ({ ...p, addressCityId: "" }));
                }}
                sx={{ mt: 1, ...whiteInputSx, ...cyanInputSx }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) => {
                    if (!addressStateId) {
                      return <Typography sx={{ color: "text.faint" }}>Select a state first</Typography>;
                    }
                    if (citiesLoading || citiesFetching) {
                      return (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CircularProgress size={18} />
                          <Typography sx={{ color: "text.muted" }}>Loading cities…</Typography>
                        </Stack>
                      );
                    }
                    if (!v) {
                      return <Typography sx={{ color: "text.faint" }}>Select city…</Typography>;
                    }
                    return locationRowName(selectedCity) || v;
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select city…</em>
                </MenuItem>
                {cities.map((c) => {
                  const cid = locationRowId(c);
                  if (!cid) return null;
                  return (
                    <MenuItem key={cid} value={cid}>
                      {locationRowName(c)}
                    </MenuItem>
                  );
                })}
              </TextField>
              {errors.addressCityId && <FormHelperText error sx={{ mt: 0.5 }}>{errors.addressCityId}</FormHelperText>}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={filledFieldWrapSx(theme, !!zipCode.trim())}>
                <RequiredLabel>Zip / Postal Code</RequiredLabel>
                <TextField
                  placeholder="e.g., 10001"
                  fullWidth
                  value={zipCode}
                  onChange={(e) => { set({ zipCode: e.target.value }); setErrors((p) => ({ ...p, zipCode: "" })); }}
                  sx={{ mt: 1, ...whiteInputSx, ...cyanInputSx }}
                />
                {errors.zipCode && <FormHelperText error sx={{ mt: 0.5 }}>{errors.zipCode}</FormHelperText>}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* REGA Details */}
        <Box
          sx={{
            mt: 4,
            p: 2.5,
            borderRadius: 2,
            bgcolor: (theme) => theme.palette.brand.cyanSoft,
            border: (theme) => `1px solid ${theme.palette.accent.teal.border}`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 16,
              color: (theme) => theme.palette.accent.teal.text,
              mb: 2,
            }}
          >
            REGA Details
          </Typography>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={filledFieldWrapSx(theme, !!regaDeedNumber.trim())}>
                <RequiredLabel>Deed Number</RequiredLabel>
                <TextField
                  placeholder="e.g., 46545645"
                  fullWidth
                  value={regaDeedNumber}
                  onChange={(e) => { set({ regaDeedNumber: e.target.value }); setErrors((p) => ({ ...p, regaDeedNumber: "" })); }}
                  sx={{ mt: 1, ...whiteInputSx, ...cyanInputSx }}
                />
                {errors.regaDeedNumber && <FormHelperText error sx={{ mt: 0.5 }}>{errors.regaDeedNumber}</FormHelperText>}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={filledFieldWrapSx(theme, !!regaCrNumber.trim())}>
                <RequiredLabel>CR Number</RequiredLabel>
                <TextField
                  placeholder="e.g., 45465897"
                  fullWidth
                  value={regaCrNumber}
                  onChange={(e) => { set({ regaCrNumber: e.target.value }); setErrors((p) => ({ ...p, regaCrNumber: "" })); }}
                  sx={{ mt: 1, ...whiteInputSx, ...cyanInputSx }}
                />
                {errors.regaCrNumber && <FormHelperText error sx={{ mt: 0.5 }}>{errors.regaCrNumber}</FormHelperText>}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={filledFieldWrapSx(theme, !!regaOwnerName.trim())}>
                <RequiredLabel>Owner Name</RequiredLabel>
                <TextField
                  placeholder="e.g., owner"
                  fullWidth
                  value={regaOwnerName}
                  onChange={(e) => { set({ regaOwnerName: e.target.value }); setErrors((p) => ({ ...p, regaOwnerName: "" })); }}
                  sx={{ mt: 1, ...whiteInputSx, ...cyanInputSx }}
                />
                {errors.regaOwnerName && <FormHelperText error sx={{ mt: 0.5 }}>{errors.regaOwnerName}</FormHelperText>}
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <RequiredLabel>Rega Certificates</RequiredLabel>
              <Typography sx={{ fontSize: 13, color: "text.muted", mt: 0.5, mb: 1.25 }}>
                Upload PDF, JPG, or PNG (Max {MAX_FILE_MB}MB)
              </Typography>
              <input
                ref={regaCertInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                hidden
                onChange={(e) => handleRegaCertificateUpload(e.target.files?.[0])}
              />
              <Paper
                component="button"
                type="button"
                onClick={() => regaCertInputRef.current?.click()}
                elevation={0}
                sx={{
                  width: "100%",
                  minHeight: 120,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  p: 2,
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: TEAL,
                  },
                  ...filledUploadDropzoneSx(
                    theme,
                    Boolean(regaCertificateFile || draft._editMeta?.regaCertificateUrl),
                    theme.palette.accent.teal.dashedBorder
                  ),
                }}
              >
                <Stack alignItems="center" spacing={1}>
                  <ArrowUpwardIcon sx={{ color: TEAL, fontSize: 30 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.neutral" }}>
                    {regaCertificateFile?.name
                      ? regaCertificateFile.name
                      : draft._editMeta?.regaCertificateUrl
                        ? "Current REGA certificate on file"
                      : "Click to upload REGA certificate"}
                  </Typography>
                </Stack>
              </Paper>
              {errors.regaCertificateFile && (
                <FormHelperText error sx={{ mt: 0.5 }}>{errors.regaCertificateFile}</FormHelperText>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Property Specifications */}
        <Box
          sx={{
            mt: 4,
            p: 2.5,
            borderRadius: 2,
            bgcolor: (theme) => theme.palette.brand.cyanSoft,
            border: (theme) => `1px solid ${theme.palette.accent.teal.border}`,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 16,
              color: (theme) => theme.palette.accent.teal.text,
              mb: 2,
            }}
          >
            Property Specifications
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={filledFieldWrapSx(theme, !!propertyType)}>
                <RequiredLabel>Property Type</RequiredLabel>
                <TextField
                  select
                  fullWidth
                  value={propertyType}
                  onChange={(e) => { set({ propertyType: e.target.value }); setErrors((p) => ({ ...p, propertyType: "" })); }}
                  sx={{ mt: 1, ...whiteInputSx, ...cyanInputSx }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (v) =>
                      v ? (
                        v
                      ) : (
                        <Typography sx={{ color: "text.faint" }}>Select type</Typography>
                      ),
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select type</em>
                  </MenuItem>
                  {propertyTypeOptions.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
                {errors.propertyType && <FormHelperText error sx={{ mt: 0.5 }}>{errors.propertyType}</FormHelperText>}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={filledFieldWrapSx(
                  theme,
                  Boolean(String(sizeValue ?? "").trim())
                )}
              >
              <Typography sx={labelSx}>Size / Area</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                  placeholder="e.g. 1500"
                  fullWidth
                  value={sizeValue}
                  onChange={(e) =>
                    set({ sizeValue: e.target.value.replace(/[^\d.]/g, "") })
                  }
                  sx={{ ...whiteInputSx, ...cyanInputSx }}
                />
                <TextField
                  select
                  value={sizeUnit}
                  onChange={(e) => set({ sizeUnit: e.target.value })}
                  sx={{ minWidth: 100, ...whiteInputSx, ...cyanInputSx }}
                >
                  {sizeUnitEntries.map(({ key, label }) => (
                    <MenuItem key={key} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Features & Amenities */}
        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "text.primary", mb: 2 }}>
            Features &amp; Amenities
          </Typography>
          <Grid container spacing={1.5}>
            {featureOptions.map((label) => {
              const Icon = iconForAmenityLabel(label);
              const selected = selectedAmenities.has(label);
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={label}>
                  <Paper
                    component="button"
                    type="button"
                    onClick={() => toggleAmenity(label)}
                    elevation={0}
                    sx={{
                      width: "100%",
                      p: 1.75,
                      borderRadius: "16px",
                      cursor: "pointer",
                      border: `2px solid ${
                        selected ? TEAL : theme.palette.border.light
                      }`,
                      bgcolor: "background.paper",
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      transition: "border-color 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        borderColor: selected ? TEAL : theme.palette.border.medium,
                        boxShadow: (theme) => theme.palette.shadow.cardHover,
                      },
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 22,
                        color: selected ? TEAL : theme.palette.text.muted,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "text.primary",
                        lineHeight: 1.3,
                      }}
                    >
                      {label}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 4, pt: 2 }}
        >
          <Button
            variant="outlined"
            onClick={prevStep}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: (theme) => theme.palette.border.medium,
              color: "text.neutral",
              px: 3,
              py: 1,
              "&:hover": {
                borderColor: (theme) => theme.palette.text.faint,
                bgcolor: "background.subtle",
              },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />}
            onClick={handleNext}
            sx={{
              px: 1.5,
              py: 1,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: TEAL,
              borderRadius: "12px",
              boxShadow: "none",
              "&:hover": { bgcolor: TEAL_HOVER, boxShadow: "none" },
            }}
          >
            Next Step
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
